/**
 * The house icon set — every icon built like the die.
 *
 * NAO picked the die as the reference after three live rounds: a white
 * body, the fat #12162c outline the animals wear, one colour accent where
 * the icon has a natural place for it, and generous default sizes. A
 * sticker carries its own contrast, so the same drawing works on dark
 * chrome, sunny buttons and sky chips without asking the background for
 * anything.
 */

const INK = "#12162c";
const BODY = "#fdfdff";
const SUN = "#f2cf1d";
const SKY = "#56b4e9";
const ONAIR = "#d55e00";

type IconProps = { className?: string };

const outline = {
  stroke: INK,
  strokeWidth: 2.4,
  strokeLinejoin: "round",
  strokeLinecap: "round",
} as const;

export function IconPlay({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <path
        d="M7.4 5.3c0-1.5 1.6-2.4 2.9-1.6l9.4 5.6c1.2.7 1.2 2.5 0 3.2l-9.4 5.7c-1.3.8-2.9-.1-2.9-1.6V5.3z"
        fill={BODY}
        {...outline}
      />
    </svg>
  );
}

export function IconFullscreen({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <rect x="2.8" y="4.2" width="18.4" height="15.6" rx="3.8" fill={BODY} {...outline} />
      <path d="M8.3 13.2v2.6h2.6M15.7 10.8V8.2h-2.6" fill="none" stroke={SKY} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 15.6l2.8-2.8M15.5 8.4l-2.8 2.8" fill="none" stroke={SKY} strokeWidth="2.3" strokeLinecap="round" />
    </svg>
  );
}

/** The reference sticker: NAO's die, untouched. */
export function IconDice({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <g transform="rotate(-10 12 12)">
        <rect x="3.4" y="3.4" width="17.2" height="17.2" rx="5" fill={BODY} {...outline} />
        <circle cx="8.4" cy="8.4" r="1.6" fill={INK} />
        <circle cx="15.6" cy="8.4" r="1.6" fill={INK} />
        <circle cx="12" cy="12" r="1.6" fill={ONAIR} />
        <circle cx="8.4" cy="15.6" r="1.6" fill={INK} />
        <circle cx="15.6" cy="15.6" r="1.6" fill={INK} />
      </g>
    </svg>
  );
}

/** Inviting is adding a person: white silhouette, sunny plus. */
export function IconInvite({ className = "h-5 w-5" }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <circle cx="10.2" cy="7.8" r="4.2" fill={BODY} {...outline} />
      <path
        d="M10.2 13.8c-4.4 0-7.4 2.6-7.4 5.9 0 .8.6 1.4 1.5 1.4h11.8c.9 0 1.5-.6 1.5-1.4 0-3.3-3-5.9-7.4-5.9z"
        fill={BODY}
        {...outline}
      />
      <circle cx="18.6" cy="8" r="4.2" fill={SUN} {...outline} />
      <path d="M18.6 6.2v3.6M16.8 8h3.6" stroke={INK} strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export function IconQr({ className = "h-6 w-6" }: IconProps) {
  const finder = (x: number, y: number, fill: string) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width="8.6" height="8.6" rx="2.6" fill={fill} {...outline} strokeWidth={2.2} />
      <rect x={x + 3} y={y + 3} width="2.6" height="2.6" rx="0.9" fill={INK} />
    </g>
  );

  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      {finder(1.6, 1.6, BODY)}
      {finder(13.8, 1.6, BODY)}
      {finder(1.6, 13.8, BODY)}
      {finder(13.8, 13.8, SUN)}
    </svg>
  );
}

export function IconVolume({ level, className = "h-5 w-5" }: IconProps & { level: number }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill="none">
      <path
        d="M3.2 9.4v5.2h3.2l4.6 3.9c.7.6 1.8.1 1.8-.8V6.3c0-.9-1.1-1.4-1.8-.8L6.4 9.4H3.2z"
        fill={BODY}
        {...outline}
        strokeWidth={2.2}
      />
      {level === 0 ? (
        <path d="M16.4 9.8l4.4 4.4M20.8 9.8l-4.4 4.4" stroke={ONAIR} strokeWidth="2.6" strokeLinecap="round" fill="none" />
      ) : (
        <>
          <path d="M16 9.5a3.8 3.8 0 0 1 0 5" stroke={SKY} strokeWidth="2.4" strokeLinecap="round" fill="none" />
          {level >= 0.5 && (
            <path d="M18.6 7.2a7.2 7.2 0 0 1 0 9.6" stroke={SKY} strokeWidth="2.4" strokeLinecap="round" fill="none" />
          )}
        </>
      )}
    </svg>
  );
}
